import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  RotateCcw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import {
  CEMETERIES,
  formatMoney,
  getCemetery,
  parsePlotId,
  rowBurialType,
} from '../data/cemeteries';
import {
  ORDER_STATUS,
  cancelOrder,
  getAvailability,
  getOrders,
  markOrderPaid,
  resetDemo,
  updateOrder,
} from '../lib/plotStore';
import { useInventory } from '../lib/useStore';

/**
 * Staff console. Every purchase across every cemetery: status, who bought it,
 * which sites, what type, what it cost, and the notes our staff keep on the
 * family.
 */

const STATUS_BADGES = {
  [ORDER_STATUS.PAID]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  [ORDER_STATUS.PENDING]: 'bg-amber-50 text-amber-700 border-amber-200',
  [ORDER_STATUS.CANCELLED]: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_LABELS = {
  [ORDER_STATUS.PAID]: 'Paid',
  [ORDER_STATUS.PENDING]: 'Pending',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: ORDER_STATUS.PAID, label: 'Paid' },
  { id: ORDER_STATUS.PENDING, label: 'Pending' },
  { id: ORDER_STATUS.CANCELLED, label: 'Cancelled' },
];

/** An order can span rows of different types — say so rather than guessing. */
const orderBurialType = (cemetery, plotIds) => {
  const types = new Set(plotIds.map((id) => rowBurialType(cemetery, parsePlotId(id).row)));
  if (types.size > 1) return 'Mixed';
  return types.has('cremation') ? 'Cremation' : 'Burial';
};

const formatDate = (ms) =>
  new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const AdminCemeteries = () => {
  useInventory();
  const [cemeteryId, setCemeteryId] = useState(CEMETERIES[0].id);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);

  const cemetery = getCemetery(cemeteryId);
  const counts = getAvailability(cemetery);
  const orders = getOrders(cemeteryId);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((order) => {
      if (filter !== 'all' && order.status !== filter) return false;
      if (!q) return true;
      const haystack = [
        order.id,
        order.client.firstName,
        order.client.lastName,
        order.client.email,
        order.client.phone,
        order.notes,
        ...order.plotIds,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, filter, query]);

  const revenue = orders
    .filter((o) => o.status === ORDER_STATUS.PAID)
    .reduce((sum, o) => sum + o.total, 0);
  const outstanding = orders
    .filter((o) => o.status === ORDER_STATUS.PENDING)
    .reduce((sum, o) => sum + o.total, 0);

  const handleReset = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Reset all demo data? Every order and hold will be regenerated.')) {
      resetDemo();
      setExpanded(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-4 px-4 py-6">
          <div>
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5" />
              Staff console
            </p>
            <h1 className="mt-1 font-cormorant text-3xl font-semibold text-navy-900">
              Cemetery Sales &amp; Inventory
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <label className="sr-only" htmlFor="cemetery-select">
              Cemetery
            </label>
            <select
              id="cemetery-select"
              value={cemeteryId}
              onChange={(e) => setCemeteryId(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-navy-700 focus:outline-none"
            >
              {CEMETERIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <Link
              to={`/cemeteries/${cemeteryId}`}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              View public page
            </Link>

            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
              title="Regenerate demo data"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset demo
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Inventory summary */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
          <Metric label="Total sites" value={counts.total} />
          <Metric label="Available" value={counts.available + counts.selected} />
          <Metric label="On hold" value={counts.held} tone="amber" />
          <Metric label="Pending" value={counts.pending} tone="amber" />
          <Metric label="Sold" value={counts.sold} />
          <Metric label="Revenue" value={formatMoney(revenue)} tone="navy" />
        </div>

        {outstanding > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            {formatMoney(outstanding)} outstanding across{' '}
            {orders.filter((o) => o.status === ORDER_STATUS.PENDING).length} pending{' '}
            {orders.filter((o) => o.status === ORDER_STATUS.PENDING).length === 1
              ? 'order'
              : 'orders'}
            .
          </p>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-1">
            {FILTERS.map((f) => {
              const count =
                f.id === 'all' ? orders.length : orders.filter((o) => o.status === f.id).length;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    filter === f.id
                      ? 'bg-navy-800 text-white'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {/* opacity-60 over the slate-600 chip label compounded to
                      #9199a5 on white (2.87:1) — set the de-emphasised tone
                      explicitly per branch instead of dimming the inherited one. */}
                  {f.label}{' '}
                  <span className={filter === f.id ? 'text-white/70' : 'text-slate-500'}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <label className="relative block w-full max-w-xs">
            <span className="sr-only">Search orders</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client, site, or order…"
              className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm placeholder:text-slate-500 focus:border-navy-700 focus:outline-none"
            />
          </label>
        </div>

        {/* Orders */}
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <Th>Order</Th>
                  <Th>Client</Th>
                  <Th>Sites</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Price</Th>
                  <Th>Status</Th>
                  <Th>Notes</Th>
                  <Th><span className="sr-only">Row actions</span></Th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      No orders match this view.
                    </td>
                  </tr>
                )}

                {visible.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    cemetery={cemetery}
                    expanded={expanded === order.id}
                    onToggle={() => setExpanded(expanded === order.id ? null : order.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ order, cemetery, expanded, onToggle }) => {
  const [notes, setNotes] = useState(order.notes);
  const [saved, setSaved] = useState(false);

  const saveNotes = () => {
    updateOrder(order.id, { notes });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  return (
    <>
      <tr
        className={`border-b border-slate-100 transition hover:bg-slate-50/70 ${
          expanded ? 'bg-slate-50' : ''
        }`}
      >
        <Td>
          <span className="font-medium text-slate-800">{order.id}</span>
          <span className="block text-xs text-slate-500">{formatDate(order.createdAt)}</span>
        </Td>
        <Td>
          <span className="font-medium text-slate-800">
            {order.client.firstName} {order.client.lastName}
          </span>
          <span className="block text-xs text-slate-500">{order.client.email}</span>
        </Td>
        <Td>
          <span className="text-slate-700">{order.plotIds.join(', ')}</span>
        </Td>
        <Td>
          <span className="text-slate-700">{orderBurialType(cemetery, order.plotIds)}</span>
        </Td>
        <Td className="text-right tabular-nums text-slate-800">{formatMoney(order.total)}</Td>
        <Td>
          <span
            className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${
              STATUS_BADGES[order.status]
            }`}
          >
            {STATUS_LABELS[order.status]}
          </span>
        </Td>
        <Td>
          <span className="line-clamp-1 max-w-[16rem] text-xs text-slate-500">
            {order.notes || <span className="text-slate-300">—</span>}
          </span>
        </Td>
        <Td className="text-right">
          <button
            type="button"
            onClick={onToggle}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            aria-label={expanded ? `Collapse order ${order.id}` : `Expand order ${order.id}`}
            aria-expanded={expanded}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        </Td>
      </tr>

      {expanded && (
        <tr className="border-b border-slate-100 bg-slate-50">
          <td colSpan={8} className="px-4 py-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Client */}
              <div>
                <h2 className="mb-3 text-[10px] uppercase tracking-widest text-slate-500">
                  Client information
                </h2>
                <dl className="space-y-1.5 text-sm">
                  <Row label="Name">
                    {order.client.firstName} {order.client.lastName}
                  </Row>
                  <Row label="Email">{order.client.email}</Row>
                  <Row label="Phone">{order.client.phone}</Row>
                  {order.client.street && (
                    <Row label="Address">
                      {order.client.street}
                      {order.client.city ? `, ${order.client.city}` : ''}
                      {order.client.state ? `, ${order.client.state}` : ''} {order.client.zip}
                    </Row>
                  )}
                </dl>
              </div>

              {/* Purchase */}
              <div>
                <h2 className="mb-3 text-[10px] uppercase tracking-widest text-slate-500">
                  Purchase
                </h2>
                <dl className="space-y-1.5 text-sm">
                  <Row label="Sites">{order.plotIds.join(', ')}</Row>
                  <Row label="Burial type">{orderBurialType(cemetery, order.plotIds)}</Row>
                  <Row label="Subtotal">{formatMoney(order.subtotal)}</Row>
                  <Row label="Perpetual care">{formatMoney(order.careFee)}</Row>
                  <Row label="Total">
                    <strong className="text-navy-900">{formatMoney(order.total)}</strong>
                  </Row>
                  <Row label="Payment ref">
                    {order.paymentRef || <span className="text-slate-500">Not yet paid</span>}
                  </Row>
                </dl>
              </div>

              {/* Notes + actions */}
              <div>
                <h2 className="mb-3 text-[10px] uppercase tracking-widest text-slate-500">
                  Staff notes
                </h2>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note about this family or purchase…"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-500 focus:border-navy-700 focus:outline-none focus:ring-1 focus:ring-navy-700"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={saveNotes}
                    disabled={notes === order.notes}
                    className="rounded-md bg-navy-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-navy-900 disabled:opacity-40"
                  >
                    Save note
                  </button>
                  {saved && <span className="text-xs text-emerald-600">Saved</span>}
                </div>

                <h2 className="mb-2 mt-6 text-[10px] uppercase tracking-widest text-slate-500">
                  Actions
                </h2>
                <div className="flex flex-wrap gap-2">
                  {order.status === ORDER_STATUS.PENDING && (
                    <button
                      type="button"
                      onClick={() => markOrderPaid(order.id)}
                      className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                    >
                      Mark paid — release deed
                    </button>
                  )}
                  {order.status !== ORDER_STATUS.CANCELLED && (
                    <button
                      type="button"
                      onClick={() => cancelOrder(order.id)}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      Cancel &amp; return sites to inventory
                    </button>
                  )}
                  {order.status === ORDER_STATUS.CANCELLED && (
                    <p className="text-xs text-slate-500">
                      Sites {order.plotIds.join(', ')} were returned to available inventory.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Metric = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'text-slate-800',
    amber: 'text-amber-600',
    navy: 'text-navy-800',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 font-cormorant text-3xl font-semibold ${tones[tone]}`}>{value}</p>
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th
    scope="col"
    className={`px-4 py-3 text-[10px] font-medium uppercase tracking-widest text-slate-500 ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-slate-500">{label}</dt>
    <dd className="text-right text-slate-800">{children}</dd>
  </div>
);

export default AdminCemeteries;
